import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

/**
 * Cursor is a plain api-key provider: the only field is an API key created in
 * the Cursor dashboard. There is no instance URL or account email to collect,
 * so this is the smallest credential form — a single masked token input.
 */

interface FormErrors {
  token?: string;
  submit?: string;
}

export function CursorCredentialsForm({
  onSaved,
  onCancel,
  details,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
}): JSX.Element {
  const [token, setToken] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setToken(e.target.value);
    if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
  };

  /** Reads the clipboard into the token field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setToken(text.trim());
      if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmedToken = token.trim();
    if (trimmedToken.length < 8) {
      setErrors({ token: 'Token looks too short' });
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveCursorCredentials(
        { token: trimmedToken },
        workspaceId,
        details,
      );
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cursor-token" className="text-xs">
          API key
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="cursor-token"
              type={showToken ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Cursor API key"
              value={token}
              onChange={handleTokenChange}
              disabled={submitting}
              aria-invalid={Boolean(errors.token)}
              aria-describedby={
                errors.token ? 'cursor-token-err' : 'cursor-token-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              aria-label={showToken ? 'Hide key' : 'Show key'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showToken ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={submitting}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.token ? (
          <p id="cursor-token-err" className="text-xs text-destructive">
            {errors.token}
          </p>
        ) : (
          <p
            id="cursor-token-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            In Cursor, open{' '}
            <span className="font-medium">
              Dashboard → Integrations → API Keys
            </span>{' '}
            and create a key. Paste it here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
